import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchGetCurrentUser } from './metaAPI'
import { fetchGetContact } from '../../features/customer/customerAPI'
import { getCookie } from '../../GlobalComponents/GlobalFunctions'

import {
    deleteLine,
    setReferenceNumber,
    updateAvailabilityMode,
    updateCartName,
    updateLine2,
    updateShippingInfo,
    updateValidUntilDate,
} from '../order/orderSlice'

import {
    acquireOrderLock,
    forceReleaseOrderLock,
    releaseOrderLock,
    renewOrderLock,
} from '../lock/lockSlice'

export enum MODALS {
    NONE = 'NONE',
    UPDATE = 'UPDATE', // Account identifier
    CUSTOMER_TYPE = 'CUSTOMER_TYPE', // Customer type
    UNIQUE_CUSTOMER_NAME = 'UNIQUE_CUSTOMER_NAME', // Unique Customer Name
    MODIFY = 'MODIFY',
    RECIPIENT = 'RECIPIENT',
    ADD_CONTACT = 'ADD_CONTACT',
}

export enum LOADING_STATES {
    INIT = 'INIT', // nothing tried
    LOADING = 'LOADING',
    DONE = 'DONE',
    FAILED = 'FAILED',
}

let user: ReturnType<typeof fetchGetCurrentUser> | null = null
export const getCurrentUser = async () => {
    if (user) return await user
    user = fetchGetCurrentUser()
    return await user
}

let webUser: ReturnType<typeof fetchGetContact> | null = null
export const getCurrentWebUser = async () => {
    if (webUser) return await webUser

    let webContactKey = getCookie('WebContactKey')
    if (webContactKey !== '' && parseInt(webContactKey) > 0) {
        webUser = fetchGetContact(undefined, parseInt(webContactKey))
        return await webUser
    }

    return null
}

interface LockInfo {
    CurrentUser: string
    HasLock: boolean
    LockAcquired: Date
    LockOwner: string
    LockKey: string
}

export interface MetaState {
    currentModal: MODALS
    historyOpen: boolean
    customerMode: boolean
    previewMode: boolean
    renewsSinceLastChange: number
    lock?: LockInfo
}

const initialState: MetaState = {
    currentModal: MODALS.NONE,
    historyOpen: false,
    customerMode: false,
    previewMode: false,
    renewsSinceLastChange: 0,
}

export const metaSlice = createSlice({
    name: 'meta',
    initialState,
    reducers: {
        setModal: (state, action: PayloadAction<MODALS>) => {
            state.currentModal = action.payload
        },
        closeModals: (state) => {
            state.currentModal = MODALS.NONE
        },
        setHistoryOpen: (state, action) => {
            state.historyOpen = action.payload
        },
        setCustomerMode: (state, action) => {
            state.customerMode = action.payload
        },
        setPreviewMode: (state, action) => {
            state.previewMode = action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addCase(acquireOrderLock.fulfilled, (state, action) => {
            let lock = action.payload
            state.lock = lock
            state.renewsSinceLastChange = 0
        })
        builder.addCase(renewOrderLock.fulfilled, (state, action) => {
            state.renewsSinceLastChange += 1
        })
        builder.addCase(releaseOrderLock.fulfilled, (state, action) => {
            state.renewsSinceLastChange = 0

            state.lock = undefined
        })
        builder.addCase(forceReleaseOrderLock.fulfilled, (state, action) => {
            state.renewsSinceLastChange = 0
            state.lock = undefined
        })
        // watching order events here
        builder.addCase(updateLine2, (state, action) => {
            state.renewsSinceLastChange = 0
        })
        builder.addCase(updateValidUntilDate.type, (state, action) => {
            state.renewsSinceLastChange = 0
        })
        builder.addCase(updateAvailabilityMode.type, (state, action) => {
            state.renewsSinceLastChange = 0
        })
        builder.addCase(deleteLine.type, (state, action) => {
            state.renewsSinceLastChange = 0
        })
        builder.addCase(setReferenceNumber.type, (state, action) => {
            state.renewsSinceLastChange = 0
        })
        builder.addCase(updateCartName.type, (state, action) => {
            state.renewsSinceLastChange = 0
        })
        builder.addCase(updateShippingInfo.type, (state, action) => {
            state.renewsSinceLastChange = 0
        })
    },
})

export const { setModal, setHistoryOpen, setCustomerMode, setPreviewMode, closeModals } =
    metaSlice.actions

export default metaSlice.reducer
